import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Calendar,
  Plus,
  Trash2,
  Edit3,
  Phone,
  MapPin,
  Mail,
  User,
  Clock,
  Star,
  StarOff,
  X,
  Check,
  Loader2,
  Building,
  CalendarDays,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipi di visita predefiniti
const TIPI_VISITA = [
  { value: 'controllo', label: 'Controllo medico', emoji: '🩺' },
  { value: 'fisioterapia', label: 'Fisioterapia', emoji: '💆' },
  { value: 'specialista', label: 'Visita specialistica', emoji: '👨‍⚕️' },
  { value: 'esami', label: 'Esami/Analisi', emoji: '🔬' },
  { value: 'altro', label: 'Altro', emoji: '📋' }
];

// Ruoli specialisti predefiniti
const RUOLI_SPECIALISTI = [
  'Medico di base',
  'Cardiologo',
  'Fisioterapista',
  'Neurologo',
  'Ortopedico',
  'Infermiere',
  'OSS',
  'Altro'
];

// ============================================
// MODAL NUOVA VISITA
// ============================================
const VisitaModal = ({ isOpen, onClose, onSave, visita = null, specialisti = [] }) => {
  const [formData, setFormData] = useState({
    titolo: '',
    data_ora: '',
    tipo_visita: 'controllo',
    luogo: '',
    indirizzo: '',
    note: '',
    specialista_id: ''
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (visita) {
      setFormData({
        titolo: visita.titolo || '',
        data_ora: visita.data_ora ? visita.data_ora.slice(0, 16) : '',
        tipo_visita: visita.tipo_visita || 'controllo',
        luogo: visita.luogo || '',
        indirizzo: visita.indirizzo || '',
        note: visita.note || '',
        specialista_id: visita.specialista_id || ''
      });
    } else {
      setFormData({
        titolo: '',
        data_ora: '',
        tipo_visita: 'controllo',
        luogo: '',
        indirizzo: '',
        note: '',
        specialista_id: ''
      });
    }
  }, [visita, isOpen]);
  
  const handleSubmit = async () => {
    if (!formData.titolo || !formData.data_ora) {
      toast.error('Inserisci titolo e data');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {visita ? 'Modifica Visita' : 'Nuova Visita'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Titolo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
            <input
              type="text"
              value={formData.titolo}
              onChange={(e) => setFormData({...formData, titolo: e.target.value})}
              placeholder="es. Visita cardiologica"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Data e ora */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data e Ora *</label>
            <input
              type="datetime-local"
              value={formData.data_ora}
              onChange={(e) => setFormData({...formData, data_ora: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Tipo visita */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={formData.tipo_visita}
              onChange={(e) => setFormData({...formData, tipo_visita: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
            >
              {TIPI_VISITA.map(tipo => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.emoji} {tipo.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Specialista (se ci sono) */}
          {specialisti.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialista</label>
              <select
                value={formData.specialista_id}
                onChange={(e) => setFormData({...formData, specialista_id: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
              >
                <option value="">Seleziona specialista...</option>
                {specialisti.map(spec => (
                  <option key={spec.id} value={spec.id}>
                    {spec.nome} - {spec.ruolo}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Luogo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Luogo</label>
            <input
              type="text"
              value={formData.luogo}
              onChange={(e) => setFormData({...formData, luogo: e.target.value})}
              placeholder="es. Ospedale San Raffaele"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Indirizzo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
            <input
              type="text"
              value={formData.indirizzo}
              onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
              placeholder="es. Via Roma 123, Milano"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({...formData, note: e.target.value})}
              placeholder="Informazioni aggiuntive..."
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none resize-none"
            />
          </div>
        </div>
        
        <div className="p-4 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MODAL NUOVO SPECIALISTA
// ============================================
const SpecialistaModal = ({ isOpen, onClose, onSave, specialista = null }) => {
  const [formData, setFormData] = useState({
    nome: '',
    ruolo: '',
    telefono: '',
    telefono_emergenza: '',
    email: '',
    indirizzo: '',
    citta: '',
    note: '',
    is_primary: false
  });
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (specialista) {
      setFormData({
        nome: specialista.nome || '',
        ruolo: specialista.ruolo || '',
        telefono: specialista.telefono || '',
        telefono_emergenza: specialista.telefono_emergenza || '',
        email: specialista.email || '',
        indirizzo: specialista.indirizzo || '',
        citta: specialista.citta || '',
        note: specialista.note || '',
        is_primary: specialista.is_primary || false
      });
    } else {
      setFormData({
        nome: '',
        ruolo: '',
        telefono: '',
        telefono_emergenza: '',
        email: '',
        indirizzo: '',
        citta: '',
        note: '',
        is_primary: false
      });
    }
  }, [specialista, isOpen]);
  
  const handleSubmit = async () => {
    if (!formData.nome || !formData.ruolo) {
      toast.error('Inserisci nome e ruolo');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {specialista ? 'Modifica Specialista' : 'Nuovo Specialista'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({...formData, nome: e.target.value})}
              placeholder="es. Dott. Mario Rossi"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Ruolo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo/Specializzazione *</label>
            <select
              value={formData.ruolo}
              onChange={(e) => setFormData({...formData, ruolo: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none bg-white"
            >
              <option value="">Seleziona...</option>
              {RUOLI_SPECIALISTI.map(ruolo => (
                <option key={ruolo} value={ruolo}>{ruolo}</option>
              ))}
            </select>
          </div>
          
          {/* Telefono principale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
            <input
              type="tel"
              value={formData.telefono}
              onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              placeholder="es. +39 02 1234567"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Telefono emergenza */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono Emergenza</label>
            <input
              type="tel"
              value={formData.telefono_emergenza}
              onChange={(e) => setFormData({...formData, telefono_emergenza: e.target.value})}
              placeholder="es. +39 333 1234567"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="es. dott.rossi@email.it"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Indirizzo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
            <input
              type="text"
              value={formData.indirizzo}
              onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
              placeholder="es. Via Roma 123"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Città */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
            <input
              type="text"
              value={formData.citta}
              onChange={(e) => setFormData({...formData, citta: e.target.value})}
              placeholder="es. Milano"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none"
            />
          </div>
          
          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({...formData, note: e.target.value})}
              placeholder="Informazioni aggiuntive..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#4A7C59] focus:ring-2 focus:ring-[#4A7C59]/20 outline-none resize-none"
            />
          </div>
          
          {/* Contatto prioritario */}
          <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200">
            <input
              type="checkbox"
              id="is_primary"
              checked={formData.is_primary}
              onChange={(e) => setFormData({...formData, is_primary: e.target.checked})}
              className="w-5 h-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
            />
            <label htmlFor="is_primary" className="flex-1">
              <span className="font-medium text-yellow-800">Contatto prioritario</span>
              <p className="text-xs text-yellow-600">Mostrato in cima nella lista SOS</p>
            </label>
            <Star className={`w-5 h-5 ${formData.is_primary ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
          </div>
        </div>
        
        <div className="p-4 border-t flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annulla
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="flex-1 bg-[#4A7C59] hover:bg-[#3A6345] text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Salva
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const GestioneVisiteTab = ({ famiglia }) => {
  const [activeTab, setActiveTab] = useState('visite'); // 'visite' | 'specialisti'
  const [visite, setVisite] = useState([]);
  const [specialisti, setSpecialisti] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showVisitaModal, setShowVisitaModal] = useState(false);
  const [showSpecialistaModal, setShowSpecialistaModal] = useState(false);
  const [editingVisita, setEditingVisita] = useState(null);
  const [editingSpecialista, setEditingSpecialista] = useState(null);
  
  // Load data
  const loadData = useCallback(async () => {
    if (!famiglia?.id) return;
    setLoading(true);
    
    try {
      // Load visite
      const visiteRes = await fetch(`${API_URL}/api/visite/${famiglia.id}?include_past=true`);
      if (visiteRes.ok) {
        const data = await visiteRes.json();
        setVisite(data.visite || []);
      }
      
      // Load specialisti
      const specRes = await fetch(`${API_URL}/api/visite/specialisti/${famiglia.id}`);
      if (specRes.ok) {
        const data = await specRes.json();
        setSpecialisti(data.specialisti || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [famiglia?.id]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // CRUD Visite
  const handleSaveVisita = async (data) => {
    const url = editingVisita 
      ? `${API_URL}/api/visite/${editingVisita.id}`
      : `${API_URL}/api/visite/`;
    
    const method = editingVisita ? 'PUT' : 'POST';
    const body = editingVisita 
      ? data 
      : { ...data, famiglia_id: famiglia.id };
    
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    if (res.ok) {
      toast.success(editingVisita ? 'Visita aggiornata' : 'Visita aggiunta');
      loadData();
      setEditingVisita(null);
    } else {
      throw new Error('Errore');
    }
  };
  
  const handleDeleteVisita = async (id) => {
    if (!confirm('Eliminare questa visita?')) return;
    
    const res = await fetch(`${API_URL}/api/visite/${id}`, { method: 'DELETE' });
    if (res.ok) {
      toast.success('Visita eliminata');
      loadData();
    }
  };
  
  // CRUD Specialisti
  const handleSaveSpecialista = async (data) => {
    const url = editingSpecialista 
      ? `${API_URL}/api/visite/specialisti/${editingSpecialista.id}`
      : `${API_URL}/api/visite/specialisti`;
    
    const method = editingSpecialista ? 'PUT' : 'POST';
    const body = editingSpecialista 
      ? data 
      : { ...data, famiglia_id: famiglia.id };
    
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    if (res.ok) {
      toast.success(editingSpecialista ? 'Specialista aggiornato' : 'Specialista aggiunto');
      loadData();
      setEditingSpecialista(null);
    } else {
      throw new Error('Errore');
    }
  };
  
  const handleDeleteSpecialista = async (id) => {
    if (!confirm('Eliminare questo specialista?')) return;
    
    const res = await fetch(`${API_URL}/api/visite/specialisti/${id}`, { method: 'DELETE' });
    if (res.ok) {
      toast.success('Specialista eliminato');
      loadData();
    }
  };
  
  // Render visita card
  const renderVisita = (visita) => {
    const tipoInfo = TIPI_VISITA.find(t => t.value === visita.tipo_visita) || TIPI_VISITA[4];
    const dataVisita = parseISO(visita.data_ora);
    const isPast = isBefore(dataVisita, startOfDay(new Date()));
    
    return (
      <div 
        key={visita.id} 
        className={`bg-white rounded-xl p-4 border ${isPast ? 'border-gray-200 opacity-60' : 'border-[#E5E7EB]'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
            isPast ? 'bg-gray-100' : 'bg-[#EBF5FB]'
          }`}>
            {tipoInfo.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-bold ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
              {visita.titolo}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
              <Calendar className="w-4 h-4" />
              <span>{format(dataVisita, "d MMMM yyyy 'alle' HH:mm", { locale: it })}</span>
            </div>
            {visita.luogo && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                <Building className="w-4 h-4" />
                <span>{visita.luogo}</span>
              </div>
            )}
            {visita.specialista && (
              <div className="flex items-center gap-2 text-sm text-[#4A7C59] mt-1">
                <User className="w-4 h-4" />
                <span>{visita.specialista.nome}</span>
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => {
                setEditingVisita(visita);
                setShowVisitaModal(true);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Edit3 className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => handleDeleteVisita(visita.id)}
              className="p-2 hover:bg-red-50 rounded-lg"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
        
        {visita.indirizzo && (
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(visita.indirizzo)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 mt-3 p-2 bg-blue-50 rounded-lg text-sm text-blue-700 hover:bg-blue-100"
          >
            <MapPin className="w-4 h-4" />
            <span className="truncate">{visita.indirizzo}</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </a>
        )}
      </div>
    );
  };
  
  // Render specialista card
  const renderSpecialista = (spec) => (
    <div 
      key={spec.id} 
      className={`bg-white rounded-xl p-4 border-2 ${spec.is_primary ? 'border-yellow-300' : 'border-gray-200'}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          spec.is_primary ? 'bg-yellow-100' : 'bg-gray-100'
        }`}>
          <User className={`w-6 h-6 ${spec.is_primary ? 'text-yellow-600' : 'text-gray-600'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-900">{spec.nome}</p>
            {spec.is_primary && (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            )}
          </div>
          <p className="text-sm text-[#4A7C59]">{spec.ruolo}</p>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => {
              setEditingSpecialista(spec);
              setShowSpecialistaModal(true);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Edit3 className="w-4 h-4 text-gray-500" />
          </button>
          <button
            onClick={() => handleDeleteSpecialista(spec.id)}
            className="p-2 hover:bg-red-50 rounded-lg"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>
      
      <div className="mt-3 grid grid-cols-2 gap-2">
        {spec.telefono && (
          <a href={`tel:${spec.telefono}`} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm text-green-700 hover:bg-green-100">
            <Phone className="w-4 h-4" />
            <span className="truncate">{spec.telefono}</span>
          </a>
        )}
        {spec.email && (
          <a href={`mailto:${spec.email}`} className="flex items-center gap-2 p-2 bg-purple-50 rounded-lg text-sm text-purple-700 hover:bg-purple-100">
            <Mail className="w-4 h-4" />
            <span className="truncate">{spec.email}</span>
          </a>
        )}
      </div>
    </div>
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A7C59]" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2C3E50]">Gestione Visite</h2>
          <p className="text-sm text-[#64748B]">Calendario appuntamenti e rubrica specialisti</p>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setActiveTab('visite')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'visite' 
              ? 'bg-white text-[#4A7C59] shadow' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CalendarDays className="w-4 h-4 inline mr-2" />
          Visite ({visite.length})
        </button>
        <button
          onClick={() => setActiveTab('specialisti')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'specialisti' 
              ? 'bg-white text-[#4A7C59] shadow' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <User className="w-4 h-4 inline mr-2" />
          Specialisti ({specialisti.length})
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'visite' && (
        <div className="space-y-4">
          <Button
            onClick={() => {
              setEditingVisita(null);
              setShowVisitaModal(true);
            }}
            className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Visita
          </Button>
          
          {visite.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessuna visita programmata</p>
              <p className="text-sm text-gray-400">Aggiungi la prima visita per iniziare</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visite.map(renderVisita)}
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'specialisti' && (
        <div className="space-y-4">
          <Button
            onClick={() => {
              setEditingSpecialista(null);
              setShowSpecialistaModal(true);
            }}
            className="w-full bg-[#4A7C59] hover:bg-[#3A6345] text-white gap-2"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Specialista
          </Button>
          
          {specialisti.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessun specialista salvato</p>
              <p className="text-sm text-gray-400">Aggiungi i contatti dei medici e specialisti</p>
            </div>
          ) : (
            <div className="space-y-3">
              {specialisti.map(renderSpecialista)}
            </div>
          )}
        </div>
      )}
      
      {/* Modals */}
      <VisitaModal
        isOpen={showVisitaModal}
        onClose={() => {
          setShowVisitaModal(false);
          setEditingVisita(null);
        }}
        onSave={handleSaveVisita}
        visita={editingVisita}
        specialisti={specialisti}
      />
      
      <SpecialistaModal
        isOpen={showSpecialistaModal}
        onClose={() => {
          setShowSpecialistaModal(false);
          setEditingSpecialista(null);
        }}
        onSave={handleSaveSpecialista}
        specialista={editingSpecialista}
      />
    </div>
  );
};

export default GestioneVisiteTab;
